/* =============================================================
   Account management: the cross-app inventory on /account, workspace
   management (rename, delete, leave, roles, remove member), and
   deleting an assessment result. API in api/src/functions/account.ts.
   ============================================================ */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import type { OrgRole } from './database.types';

export interface Inventory {
  /** site_slug: the site page (/interaction-map/p/<slug>/) a map of mine is the working copy of (migration 021). */
  maps: { id: string; title: string; node_count: number; edge_count: number; updated_at: string; role: 'owner' | 'edit' | 'view'; owner_name: string | null; owner_email: string; share_count: number; site_slug?: string | null }[];
  results: { id: string; completed_at: string; overall_score: number | null; org_id: string | null; org_name: string | null }[];
  draft: { updated_at: string; stage_idx: number } | null;
  workspaces: { id: string; name: string; kind: string | null; role: OrgRole; joined_at: string; member_count: number; checklist_count: number }[];
  checklists: { id: string; org_id: string; name: string; pathway: string | null; updated_at: string; org_name: string }[];
  invites: { token: string; role: OrgRole; created_at: string; expires_at: string; org_name: string }[];
}

export function useInventory(userId: string | undefined) {
  return useQuery<Inventory>({
    queryKey: ['inventory', userId], staleTime: 0,
    enabled: !!userId,
    queryFn: () => apiFetch<Inventory>('/api/me/inventory'),
  });
}

/* Everything that changes workspaces or results refreshes the views that show them. */
function useAccountMutation<A, R = unknown>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of ['plan', 'inventory', 'my-workspaces', 'workspace', 'workspace-members', 'workspace-invites', 'checklists', 'assessment-history', 'psych-landing', 'assessment-latest', 'assessment-latest-summary'])
        qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export const useRenameWorkspace = () =>
  useAccountMutation((a: { orgId: string; name: string }) => apiFetch(`/api/workspaces/${a.orgId}`, { method: 'PUT', body: { name: a.name } }));
export const useDeleteWorkspace = () =>
  useAccountMutation((orgId: string) => apiFetch(`/api/workspaces/${orgId}`, { method: 'DELETE' }));
export const useLeaveWorkspace = () =>
  useAccountMutation((orgId: string) => apiFetch(`/api/workspaces/${orgId}/leave`, { method: 'POST' }));
export const useSetMemberRole = () =>
  useAccountMutation((a: { orgId: string; userId: string; role: OrgRole }) =>
    apiFetch(`/api/workspaces/${a.orgId}/members/${a.userId}`, { method: 'PUT', body: { role: a.role } }));
export const useRemoveMember = () =>
  useAccountMutation((a: { orgId: string; userId: string }) => apiFetch(`/api/workspaces/${a.orgId}/members/${a.userId}`, { method: 'DELETE' }));
export const useDeleteResult = () =>
  useAccountMutation((id: string) => apiFetch(`/api/assessments/${id}`, { method: 'DELETE' }));

/* ---------- Plan, profile, my data ---------- */

export interface Plan {
  plan: 'member' | 'paid';
  maps: number;
  checklists: number;
  invites: number;
  limits: { maps: number; checklists: number; invites: number };
}

export function usePlan(userId: string | undefined) {
  return useQuery<Plan>({ queryKey: ['plan', userId], staleTime: 0, enabled: !!userId, queryFn: () => apiFetch<Plan>('/api/me/plan') });
}

export const useUpdateName = () =>
  useAccountMutation((full_name: string) => apiFetch<{ full_name: string; signin_updated: boolean }>('/api/me/profile', { method: 'PUT', body: { full_name } }));

export const useWithdrawInvite = () =>
  useAccountMutation((a: { orgId: string; inviteId: string }) => apiFetch(`/api/workspaces/${a.orgId}/invites/${a.inviteId}`, { method: 'DELETE' }));

/** Download everything held about you as a JSON file. */
export async function downloadMyData(): Promise<void> {
  const data = await apiFetch<unknown>('/api/me/export');
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `prajnainnovates-my-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export function deleteMyAccount() {
  return apiFetch<{ deleted: boolean; signin_removed: boolean }>('/api/me', { method: 'DELETE', body: { confirm: 'DELETE' } });
}

/* ---------- Apps on my profile, and the weekly newsletter (migration 022) ---------- */

export type AppKey = 'map' | 'assessment' | 'checklist';
export const APP_KEYS: AppKey[] = ['map', 'assessment', 'checklist'];

/** The apps a member has added to their profile; apps null = never chosen. */
export function useMyApps(userId: string | undefined) {
  return useQuery<{ apps: AppKey[] | null }>({
    queryKey: ['my-apps', userId],
    staleTime: 0, // re-read when the tab regains focus, so a change made in another tab isn't overwritten
    enabled: !!userId,
    queryFn: () => apiFetch<{ apps: AppKey[] | null }>('/api/me/apps'),
  });
}

export function useSetMyApps(userId: string | undefined) {
  const qc = useQueryClient();
  const key = ['my-apps', userId];
  return useMutation({
    mutationKey: ['set-my-apps'],
    scope: { id: 'my-apps' }, // saves run one at a time, in the order they were made
    mutationFn: (apps: AppKey[]) => apiFetch<{ apps: AppKey[] }>('/api/me/apps', { method: 'PUT', body: { apps } }),
    onMutate: async (apps) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ apps: AppKey[] | null }>(key);
      qc.setQueryData(key, { apps });
      return { prev };
    },
    onError: (_e, _apps, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      else qc.resetQueries({ queryKey: key, exact: true }); // nothing to restore: read it again
    },
    /* Re-read once the last queued save is done, so an earlier answer can't flash back over a later click. */
    onSettled: () => {
      if (qc.isMutating({ mutationKey: ['set-my-apps'] }) <= 1) return qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** Until a member chooses, their profile shows the apps they already have work in (none for a new member). */
export function appsWithWork(inv: Inventory): AppKey[] {
  return APP_KEYS.filter((k) =>
    k === 'map' ? inv.maps.length > 0 : k === 'assessment' ? inv.results.length > 0 || !!inv.draft : inv.workspaces.length > 0 || inv.checklists.length > 0,
  );
}

export interface NewsletterStatus {
  subscribed: boolean;
  since: string | null;
}

export function useNewsletter(userId: string | undefined) {
  return useQuery<NewsletterStatus>({
    queryKey: ['newsletter', userId],
    staleTime: 0, // the unsubscribe page or another tab may have changed it
    enabled: !!userId,
    queryFn: () => apiFetch<NewsletterStatus>('/api/me/newsletter'),
  });
}

export function useSetNewsletter(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscribed: boolean) => apiFetch<NewsletterStatus>('/api/me/newsletter', { method: 'PUT', body: { subscribed } }),
    onSuccess: (d) => qc.setQueryData(['newsletter', userId], d),
  });
}
